import { Box, Button, Flex, Paper, Tooltip } from "@mantine/core";
import { IssueViewEnum } from "@mcapps/shared/enums";
import { AppModalEnums, MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import { FlatHierarchy, IMinUserDepricated, IPNSProps } from "@mctypes/global.types";
import { IIssueApi, ITableTemplate } from "@schemas/issueSchema";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { ReactFlowProvider } from "@xyflow/react";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  useGetMcbimSpacesQuery,
  useGetProjectByProjectIdIssueboardAndIssueBoardIdQuery,
  useGetProjectByProjectIdIssuesOnBoardOldCompletedCountQuery,
  useGetProjectByProjectIdIssuesOnBoardOldCompletedQuery,
  useGetProjectByProjectIdIssuesOnBoardQuery,
  usePostProjectByProjectIdIssueboardOtherAttributesConfigGetSumsByFormulaMutation,
} from "../../../../../openapi";
import { FailedAlert } from "../../../../components";
import HierarchyTreeSelector from "../../../../components/HierarchyTreeSelector/HierarchyTreeSelector";
import { addAppModalThunk } from "../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import ErrorBoundary from "../../../../util/ErrorBoundary";
import { projectHasADefaultArcgisMap } from "../../../utils";
import { doLogUsage } from "../../admin/restQueries";
import { NoGisGroup, NoGisMap } from "../../gis/ArcGisMap";
import { useDoUpdateIssueMutation, useGetUniqueTagsOnBoardQuery } from "../issueRTKLegacy";
import {
  getIssueBoardFormulaSumsCacheKey,
  selectActiveTableTemplate,
  selectConnectedBimSpacesDisabled,
  selectFilteredIssuesOnBoard,
  selectIncludeAllCompletedIssuesOnBoard,
  selectIssueBoardContainsAutomaticFormula,
  selectIssueBoardScopedMembers,
  selectIssuesOrTasksOnBoardArgs,
  selectOldCompletedIssuesOnBoardArgs,
} from "../issueSelectors";
import {
  setIncludeAllCompletedIssuesOnBoardInRedux,
  setSelectedPnsFilterItemInRedux,
  thunkOpenIssueModalById,
  updateColumnStateThunk,
} from "../issueSlice";
import { loadIssueTableTemplateThunk } from "../issueThunk";
import { getInitialIssueTableTemplate } from "../issueUtils";
import { selectIssueBoardId } from "../redux/selectors/issueCommonSelectors";
import IssueDashboardMain from "./Dashboard/IssueDashboardMain";
import { IssueDbFilterBarNew } from "./filters/IssueFilterBarNew";
import IssueFloorplan from "./Floorplan/IssueFloorplan";
import SvarGantt from "./Gant/GanttChart/SvarGantt";
import IssuesGis from "./GIS/IssuesGis";
import IssueTabActionBar from "./IssueTabActionBar";
import IssueKanban from "./Kanban/IssueKanban";
import { Lappeplan } from "./Lappeplan/Lappeplan";
import IssueTableAgGridRefactored from "./TableRefactor/IssueTableAgGridRefactored";

const IssueTab: React.FC<{ setActiveTab: (tab: string) => void }> = ({ setActiveTab }) => {
  const [tableTempalteWasSetOnStart, setTableTempalteWasSetOnStart] = useState<boolean>(false);

  const project = useAppSelector((state) => state.adminReducer.project);
  const allColumnState = useAppSelector((state) => state.issueReducerNew.allColumnState);
  const activeTableTemplateId = useAppSelector((state) => state.issueReducerNew.activeTableTemplateId);
  const activeView = useAppSelector((state) => state.issueReducerNew.activeView);
  const selectedPnsFilterItem = useAppSelector((state) => state.issueReducerNew.selectedPnsFilterItem);
  const openedModals = useAppSelector((state) => state.globalReducer.openedAppModals);
  const gridRef = useRef<AgGridReact>(null);
  const pnsItems = project?.pns as FlatHierarchy<IPNSProps>;

  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const issueBoardContainsAutomaticFormula = useAppSelector(selectIssueBoardContainsAutomaticFormula);
  const connectedBimSpacesDisabled = useAppSelector(selectConnectedBimSpacesDisabled);
  const activeTemplate = useAppSelector(selectActiveTableTemplate);
  const boardScopedMembers = useAppSelector(selectIssueBoardScopedMembers);

  const args = useAppSelector(selectIssuesOrTasksOnBoardArgs);
  const oldCompletedIssuesArgs = useAppSelector(selectOldCompletedIssuesOnBoardArgs);
  const includeAllCompletedIssuesOnBoard = useAppSelector(selectIncludeAllCompletedIssuesOnBoard);
  const formulaSumsCacheKey =
    projectId != null && issueBoardId != null
      ? getIssueBoardFormulaSumsCacheKey(projectId, issueBoardId)
      : "issue-board-formula-sums:empty";
  const [getSumsByFormula] = usePostProjectByProjectIdIssueboardOtherAttributesConfigGetSumsByFormulaMutation({
    fixedCacheKey: formulaSumsCacheKey,
  });

  const {
    isLoading: isLoadingIssues,
    error: errorIssues,
    isFetching: issueFetching,
  } = useGetProjectByProjectIdIssuesOnBoardQuery(args, {
    skip: args.projectId == null || args.issueboardId == null,
  });

  const {
    isLoading: isLoadingOldCompletedIssues,
    error: errorOldCompletedIssues,
    isFetching: oldCompletedIssuesFetching,
  } = useGetProjectByProjectIdIssuesOnBoardOldCompletedQuery(oldCompletedIssuesArgs, {
    skip:
      includeAllCompletedIssuesOnBoard !== true ||
      oldCompletedIssuesArgs.projectId == null ||
      oldCompletedIssuesArgs.issueboardId == null,
  });

  useEffect(() => {
    if (issueBoardContainsAutomaticFormula !== true || projectId == null || issueBoardId == null) return;
    void getSumsByFormula({
      projectId,
      body: {
        issueBoardId,
      },
    });
  }, [getSumsByFormula, issueBoardContainsAutomaticFormula, issueBoardId, projectId]);

  const dispatch = useAppDispatch();
  const location = useLocation();

  const {
    data: issueBoard,
    isLoading: boardLoading,
    error: boardError,
  } = useGetProjectByProjectIdIssueboardAndIssueBoardIdQuery({
    projectId: project._id,
    issueBoardId: issueBoardId,
  });

  const { data: oldCompletedIssuesCount } = useGetProjectByProjectIdIssuesOnBoardOldCompletedCountQuery(
    oldCompletedIssuesArgs,
    {
      skip:
        includeAllCompletedIssuesOnBoard === true ||
        oldCompletedIssuesArgs.projectId == null ||
        oldCompletedIssuesArgs.issueboardId == null ||
        issueBoard?.completedIssuesFetchLimitMonths == null,
    },
  );

  useGetMcbimSpacesQuery(
    { projectId },
    {
      skip:
        projectId == null ||
        project?.mcbim?.activated !== true ||
        issueBoard == null ||
        connectedBimSpacesDisabled === true,
    },
  );

  useEffect(() => {
    if (issueBoard?.completedIssuesFetchLimitMonths == null && includeAllCompletedIssuesOnBoard) {
      dispatch(setIncludeAllCompletedIssuesOnBoardInRedux(false));
    }
  }, [dispatch, includeAllCompletedIssuesOnBoard, issueBoard?.completedIssuesFetchLimitMonths]);

  useEffect(() => {
    if (issueBoardId != null && boardLoading === false && project != null && issueBoard != null) {
      doLogUsage(project, MCApplicationsUrlEnum.ISSUE, "open", issueBoard?.issueNameSetting);
    }
  }, [issueBoardId, issueBoard, boardLoading, project]);

  const [updateIssue, { error: updateIssueError }] = useDoUpdateIssueMutation();

  const filtredIssues = useAppSelector(selectFilteredIssuesOnBoard);

  useGetUniqueTagsOnBoardQuery({
    projectId: project._id,
    boardId: issueBoardId,
  });

  useEffect(() => {
    if (issueBoard != null && activeTableTemplateId == null && tableTempalteWasSetOnStart === false) {
      const activeTableTemplate = getInitialIssueTableTemplate(
        issueBoard.tableTemplates ?? [],
        issueBoard.defaultTemplate,
        location.search,
      );
      if (activeTableTemplate != null) {
        setTableByTableTemplateCb(activeTableTemplate, true);
        setTableTempalteWasSetOnStart(true);
      }
    }
  }, [issueBoard, activeTableTemplateId, location.search, tableTempalteWasSetOnStart]);

  useEffect(() => {
    if (issueBoard != null && issueBoard.defaultTemplate == null && activeTableTemplateId == null) {
      setHideTable(false);
    }
  }, [issueBoard, activeTableTemplateId]);

  //This is for changing template after table loaded. setTableByTableTemplateCb was previously called directly from tableTemplate component
  useEffect(() => {
    if (!issueBoard || !activeTemplate) return;
    const searchParams = new URLSearchParams(history.location.search);
    if (!searchParams.has("id")) {
      searchParams.set("template", activeTemplate._id);
      history.push(`${history.location.pathname}?${searchParams.toString()}`);
    }
    setHideTable(true);
    setTimeout(() => {
      setHideTable(false);
    }, 500);
  }, [activeTemplate?._id]);

  let history = useHistory();

  useEffect(() => {
    const id = new URLSearchParams(history.location.search).get("id");

    if (id?.length === 24 && openedModals?.find((item) => item.itemId === id) == null) {
      dispatch(
        addAppModalThunk({
          itemId: id,
          boardId: issueBoardId,
          projectId: project._id,
          app: AppModalEnums.ISSUE,
        }),
      );
    }
  }, [dispatch, history.location.search, issueBoardId, openedModals, project._id]);

  const setIssueIdUrlParam = useCallback(
    (issue: IIssueApi) => {
      if (issue != null) {
        dispatch(thunkOpenIssueModalById(issue._id));
      } else {
        history.push(`${history.location.pathname}`);
      }
    },
    [dispatch, history],
  );

  const [hideTable, setHideTable] = useState<boolean>(true);

  const setTableByTableTemplateCb = async (tableTemplate: ITableTemplate, onLoad?: boolean) => {
    setHideTable(true);
    const searchParams = new URLSearchParams(history.location.search);

    if (!searchParams.has("id")) {
      searchParams.set("template", tableTemplate._id);
      history.replace(`${history.location.pathname}?${searchParams.toString()}`);
    }

    if (onLoad !== true || (allColumnState[issueBoardId] == null && tableTemplate._id === issueBoard.defaultTemplate)) {
      //if template is clicked, or no column state is saved and default template is active, save the new column state
      const { tableColConfigs, tableColumns = [] } = tableTemplate;
      const columnsToSave = tableColConfigs?.length ? tableColConfigs : tableColumns.map((col) => ({ colId: col }));
      dispatch(updateColumnStateThunk(columnsToSave));
    }

    dispatch(loadIssueTableTemplateThunk(tableTemplate));

    setTimeout(() => {
      setHideTable(false);
    }, 500);
  };

  const isLoading =
    boardLoading || isLoadingIssues || issueFetching || isLoadingOldCompletedIssues || oldCompletedIssuesFetching;

  const error = boardError || updateIssueError || errorIssues || errorOldCompletedIssues;

  const selectedPnsIds = useMemo(() => {
    const id = pnsItems.find((item) => item.props.pnsId === selectedPnsFilterItem)?._id;
    return id != null ? [id] : [];
  }, [selectedPnsFilterItem, pnsItems]);
  const selectedGisMap = useAppSelector((state) => state.issueReducerNew.selectedGisMap);
  const showFetchAllIssuesBanner =
    issueBoard?.completedIssuesFetchLimitMonths != null && includeAllCompletedIssuesOnBoard !== true;
  const fetchOldCompletedIssuesLabel =
    issueBoard?.completedIssuesFetchLimitMonths != null
      ? `Hent saker eldre enn ${issueBoard.completedIssuesFetchLimitMonths} måneder${
          oldCompletedIssuesCount?.count != null ? ` (${oldCompletedIssuesCount.count})` : ""
        }`
      : "Hent saker";
  const issueTypes = useMemo(() => issueBoard?.types.map((type) => type.name) ?? [], [issueBoard?.types]);
  const updateItemMembers = useCallback(
    (itemId: string, members: IMinUserDepricated[]) => {
      updateIssue({
        projectId: project._id,
        issueBoardId: issueBoardId,
        issueId: itemId,
        attr: "itemMembers",
        value: members,
        oldValue: "",
      });
    },
    [issueBoardId, project._id, updateIssue],
  );
  const issueTableColumnProps = useMemo(
    () => ({
      issueBoard: issueBoard,
      boardBuckets: issueBoard?.buckets ?? [],
      setSelectedIssue: setIssueIdUrlParam,
      types: issueTypes,
      pnsItems: pnsItems,
      members: boardScopedMembers as unknown as IProjectMemberApiItem[],
      projectId: project._id,
      issueBoardId: issueBoardId,
      otherAttributesConfig: issueBoard?.otherAttributesConfig,
      otherAttributesTasksConfig: issueBoard?.otherAttributesTasksConfig,
      updateItemMembers,
    }),
    [
      boardScopedMembers,
      issueBoard,
      issueBoardId,
      issueTypes,
      pnsItems,
      project._id,
      setIssueIdUrlParam,
      updateItemMembers,
    ],
  );

  return (
    <>
      {error != null && <FailedAlert />}
      <>
        <IssueTabActionBar
          isLoading={isLoading}
          issueBoard={issueBoard}
          setActiveTab={setActiveTab}
          setTableByTableTemplateCb={setTableByTableTemplateCb}
          gridRef={gridRef}
        />
        <IssueDbFilterBarNew gridRef={gridRef} />
        <Flex direction="column" style={{ flex: 1, minHeight: 0, width: "100%" }}>
          <Box display="flex" py="0.5rem" style={{ flex: 1, minHeight: 0, width: "100%" }}>
            {pnsItems?.length > 0 && (
              <HierarchyTreeSelector
                isCollapsed
                style={{
                  minWidth: "200px",
                  maxWidth: "300px",
                  background: "#ededed",
                }}
                isVisible
                nodeList={pnsItems}
                idProp={"pnsId"}
                titleProp={"title"}
                onSelectionChange={(node) => dispatch(setSelectedPnsFilterItemInRedux(node[0]?.props?.pnsId))}
                selectedNodeIds={selectedPnsIds}
              />
            )}
            <>
              {activeView === "TABLE" && true && hideTable !== true && issueBoard != null && (
                <Flex direction="column" px="0.25rem" w="100%">
                  <IssueTableAgGridRefactored
                    key={issueBoardId ?? "no-board"}
                    loading={isLoading}
                    items={filtredIssues}
                    gridRef={gridRef}
                    columnProps={issueTableColumnProps}
                  />
                </Flex>
              )}
              {activeView === "KANBAN" && <IssueKanban />}
              {activeView === "NOTEBOARD" && (
                <ReactFlowProvider>
                  <Lappeplan />
                </ReactFlowProvider>
              )}
              {activeView === "GIS" && (
                <>
                  {selectedGisMap?.mapId != null && (
                    <IssuesGis
                      issues={filtredIssues}
                      projectId={project._id}
                      mapId={selectedGisMap?.mapId}
                      boardId={issueBoardId}
                    />
                  )}
                  {project.arcgis?.groupId != null && projectHasADefaultArcgisMap(project) === false && <NoGisMap />}
                  {project.arcgis?.groupId == null && <NoGisGroup />}
                </>
              )}
              {activeView === "GANTT" && (
                <ErrorBoundary>
                  <Box style={{ flexGrow: 1, overflow: "auto" }} mx="0.25rem">
                    <SvarGantt />
                  </Box>
                </ErrorBoundary>
              )}
              {activeView === IssueViewEnum.BIM && <IssueFloorplan />}
              {activeView === IssueViewEnum.DASHBOARD && (
                <IssueDashboardMain projectId={project._id} issueBoardId={issueBoardId} />
              )}
            </>
          </Box>
        </Flex>
        {showFetchAllIssuesBanner && (
          <Paper
            withBorder
            radius={0}
            px="md"
            py="xs"
            style={{
              borderLeft: 0,
              borderRight: 0,
              borderBottom: 0,
              flexShrink: 0,
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Tooltip label={'Kan endres under "Visning" under innstillinger'} withArrow>
                <Button
                  color="mc-primary"
                  size="sm"
                  variant="filled"
                  px="xs"
                  disabled={isLoading}
                  onClick={() => dispatch(setIncludeAllCompletedIssuesOnBoardInRedux(true))}
                >
                  {fetchOldCompletedIssuesLabel}
                </Button>
              </Tooltip>
              <Box />
            </Box>
          </Paper>
        )}
      </>
    </>
  );
};

export default IssueTab;
